import {
  calcularImportes,
  centimosAEuros,
  redondearACentimos,
  type LineaImporteEntrada,
  type ResultadoImportes,
} from "@shared/importes";
import { evaluarInstantanea, instantaneaCoherente } from "@shared/importesEmision";
import type { InvoiceItem } from "@shared/types";

/** Aviso que se muestra cuando la instantánea guardada al emitir no es utilizable. */
export const AVISO_IMPORTES_INVALIDOS =
  "Los importes guardados al emitir no son válidos o no coinciden con las líneas; se muestran recalculados.";

/** De dónde salen los importes que muestra el cliente. */
export type FuenteImportes =
  | "emision" // la instantánea guardada al emitir
  | "actual" // cálculo con el perfil y los impuestos de hoy
  | "instantanea-invalida"; // cálculo actual: instantánea mal formada o que las líneas no reproducen

export interface ImportesDocumento {
  fuente: FuenteImportes;
  /** Tasa de IRPF de la emisión; solo con `fuente === "emision"`. */
  irpfRateEmision?: number;
  /**
   * Con `fuente === "emision"`, si hay que mostrar la fila de IRPF: el líquido guardado
   * ya descuenta el IRPF de la instantánea, así que la fila depende de ella (`irpf !== 0`)
   * y no de `aplicarIrpf`. Sin instantánea utilizable no se rellena: decide quien llama.
   */
  mostrarIrpf?: boolean;
  /** Desglose por línea y por tipo de IVA (con los tipos guardados si hay instantánea). */
  resultado: ResultadoImportes;
  subtotal: number;
  iva: number;
  /** Con el signo del documento (negativo en un abono). */
  irpf: number;
  /** Líquido a pagar: ImporteTotal − IRPF. */
  total: number;
}

/** Documento tal como lo lee el cliente (Factura, Rectificativa o Presupuesto). */
export interface DocumentoImportes {
  verifactu?: unknown;
  importesEmision?: unknown;
  items?: InvoiceItem[];
  total?: number;
}

/**
 * Importes que muestran el PDF y los resúmenes de un documento.
 *
 * La regla de confianza es la del servidor (`evaluarInstantanea` + `instantaneaCoherente`,
 * compartidas en `shared/`):
 * - instantánea válida Y coherente (las líneas la reproducen y el líquido guardado
 *   cuadra): base, IVA, IRPF y total salen de ella, y el desglose por línea usa los
 *   tipos guardados (`resultado.lineas[i].rate`), no el perfil ni los impuestos de hoy;
 * - sin instantánea (borrador o documento anterior): cálculo actual, como siempre;
 * - instantánea mal formada, o válida pero que las líneas ya no reproducen (líneas
 *   editadas, líquido que no cuadra, `tiposIva` de otra longitud): cálculo actual, y
 *   `fuente` lo indica para avisar. El servidor rechaza ese documento.
 *
 * @param lineasActuales Líneas con el tipo de IVA de hoy (ya saneadas por quien llama).
 * @param tasaIrpfActual Tasa de IRPF de hoy, ya 0 si el documento no aplica IRPF.
 */
export function calcularImportesDocumento(
  documento: DocumentoImportes,
  lineasActuales: LineaImporteEntrada[],
  tasaIrpfActual: number,
): ImportesDocumento {
  const lectura = evaluarInstantanea(documento as Parameters<typeof evaluarInstantanea>[0]);

  if (
    lectura.estado === "valida" &&
    instantaneaCoherente({ items: documento.items ?? [], total: documento.total as number }, lectura.importes)
  ) {
    const instantanea = lectura.importes;
    // Coherente implica `tiposIva.length === items.length`: las filas usan los tipos guardados.
    const lineas = lineasActuales.map((linea, i) => ({ ...linea, rate: instantanea.tiposIva[i] }));
    return {
      fuente: "emision",
      irpfRateEmision: instantanea.irpfRate,
      mostrarIrpf: instantanea.irpf !== 0,
      resultado: calcularImportes(lineas, instantanea.irpfRate),
      subtotal: instantanea.baseImponible,
      iva: instantanea.cuotaTotal,
      irpf: instantanea.irpf,
      total: centimosAEuros(redondearACentimos(instantanea.importeTotal) - redondearACentimos(instantanea.irpf)),
    };
  }

  const resultado = calcularImportes(lineasActuales, tasaIrpfActual);
  return {
    fuente: lectura.estado === "sin-instantanea" ? "actual" : "instantanea-invalida",
    resultado,
    subtotal: centimosAEuros(resultado.baseTotalCentimos),
    iva: centimosAEuros(resultado.cuotaTotalCentimos),
    irpf: centimosAEuros(resultado.irpfCentimos),
    total: centimosAEuros(resultado.totalCentimos),
  };
}
